import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { mergeAudioFiles, checkTaskStatus } from '../../services/audioApi';
import { 
    Upload, Download, Loader2, AlertCircle, 
    CheckCircle, Music, Trash2, Plus, FileAudio, ArrowUp, ArrowDown, Play, Pause
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { Link } from 'react-router-dom';

const SubscribePrompt = () => {
    const { t } = useTranslation();
    const PLAN_PRICE = "14,90 €"; 

    return (
        <div className="flex items-center justify-center h-full p-4">
            <div className="max-w-md w-full bg-brand-dark border-2 border-brand-orange rounded-lg p-8 shadow-lg">
                <h2 className="text-center text-3xl font-bold text-brand-orange mb-4">
                    {PLAN_PRICE}/{t('subscribePrompt.perMonth')}
                </h2>
                <ul className="text-slate-200 space-y-3 list-disc list-inside mb-8">
                    <li>{t('subscribePrompt.feature1')}</li>
                    <li>{t('subscribePrompt.feature2')}</li>
                    <li>{t('subscribePrompt.feature3')}</li>
                    <li>{t('subscribePrompt.feature4')}</li>
                    <li>{t('subscribePrompt.feature5')}</li>
                </ul>
                <Link 
                    to="/pricing"
                    className="w-full block text-center px-6 py-3 bg-brand-orange text-white font-semibold rounded-md hover:bg-orange-600 transition-colors"
                >
                    {t('subscribePrompt.subscribeButton')}
                </Link>
            </div>
        </div>
    );
};

const PurchaseCreditsPrompt = () => {
    const { t } = useTranslation();
    const { user } = useAuth();

    return (
        <div className="flex flex-col items-center justify-center h-full text-center p-8">
            <h2 className="text-2xl font-semibold mb-2 text-slate-800">{t('purchaseCreditsPrompt.title')}</h2>
            <p className="text-slate-800 mb-1">{t('purchaseCreditsPrompt.yourCredits')} <span className="text-brand-orange font-bold">{user?.credits || 0}</span></p>
            <p className="text-slate-800 mb-6 max-w-md">{t('purchaseCreditsPrompt.description')}</p>
            <Link 
                to="/purchase-credits"
                className="px-8 py-3 bg-brand-orange text-white font-semibold rounded-md hover:bg-orange-600 transition-colors"
            >
                {t('purchaseCreditsPrompt.purchaseButton')}
            </Link>
        </div>
    );
};

const AudioMerger = () => {
    const { t } = useTranslation();
    const { user, setCredits } = useAuth();
    
    // --- STATE ---
    const [files, setFiles] = useState([]);
    const [error, setError] = useState(null);
    
    // Processing
    const [isProcessing, setIsProcessing] = useState(false);
    const [taskId, setTaskId] = useState(null);
    const [progressStep, setProgressStep] = useState('');
    const [progressValue, setProgressValue] = useState(0);
    const [resultUrl, setResultUrl] = useState(null);

    const pollingIntervalRef = useRef(null);

    const [playingIndex, setPlayingIndex] = useState(null);
    const audioRefs = useRef([]);

    // Cleanup
    useEffect(() => {
        return () => {
            if (pollingIntervalRef.current) clearInterval(pollingIntervalRef.current);
        };
    }, []);

    // --- LOGIC ---
    
    // Calculate total size in MB
    const totalSizeMB = files.reduce((acc, file) => acc + file.size, 0) / (1024 * 1024);
    const MAX_SIZE_MB = 50;

    const handleFileChange = (e) => {
        const newFiles = Array.from(e.target.files);
        if (newFiles.length === 0) return;

        // Calculate potential new size
        const currentSize = files.reduce((acc, f) => acc + f.size, 0);
        const newSize = newFiles.reduce((acc, f) => acc + f.size, 0);
        
        if ((currentSize + newSize) > MAX_SIZE_MB * 1024 * 1024) {
            setError(t('audioMerger.errors.tooLarge'));
            return;
        }

        setError(null);
        setFiles(prev => [...prev, ...newFiles]);
        // Reset file input value
        e.target.value = null;
    };

    const removeFile = (index) => {
        setFiles(files.filter((_, i) => i !== index));
        setError(null);
    };

    const moveFile = (index, direction) => {
        const newFiles = [...files];
        const targetIndex = index + direction;
        if (targetIndex >= 0 && targetIndex < newFiles.length) {
            [newFiles[index], newFiles[targetIndex]] = [newFiles[targetIndex], newFiles[index]];
            setFiles(newFiles);
        }
    };

    const handleProcess = async () => {
        if (files.length < 2) {
            setError(t('audioMerger.errors.notEnoughFiles'));
            return;
        }
        
        setIsProcessing(true);
        setError(null);
        setProgressValue(0);

        try {
            const response = await mergeAudioFiles(files);
            setTaskId(response.task_id);
        } catch (err) {
            setError(err.message);
            setIsProcessing(false);
        }
    };

    // Polling Logic (Identical to reference)
    useEffect(() => {
        if (taskId && isProcessing) {
            pollingIntervalRef.current = setInterval(async () => {
                try {
                    const data = await checkTaskStatus(taskId);
                    if (data.state === 'SUCCESS' || data.status === 'completed') {
                        clearInterval(pollingIntervalRef.current);
                        const payload = data.result;
			const innerResult = payload.result;

                        if (innerResult?.new_credits !== undefined) {
                            setCredits(innerResult.new_credits);
                        }
                        if (payload.status === 'completed' && innerResult?.download_url) {
                            setResultUrl(innerResult.download_url);

                            setProgressValue(100);
                            setIsProcessing(false);
                        } else {
                            setError(payload.error || t('audioMerger.errors.generic'));
                            setIsProcessing(false);
                        }
                    } else if (data.state === 'FAILURE') {
                        clearInterval(pollingIntervalRef.current);
                        setError(data.error || t('audioMerger.errors.generic'));
                        setIsProcessing(false);
                    } else {
                        if (data.info) {
                            if (data.info.step) setProgressStep(data.info.step);
                            if (data.info.progress) setProgressValue(data.info.progress);
                        }
                    }
                } catch (err) {
                    clearInterval(pollingIntervalRef.current);
                    setError(err.message);
                    setIsProcessing(false);
                }
            }, 2000);
        }
        return () => {
            if (pollingIntervalRef.current) clearInterval(pollingIntervalRef.current);
        };
    }, [taskId, isProcessing, t]);

    const handleClear = () => {
        // Stop any playing audio
        if (playingIndex !== null && audioRefs.current[playingIndex]) {
            audioRefs.current[playingIndex].pause();
            audioRefs.current[playingIndex] = null;
        }
        setPlayingIndex(null);
        
        setFiles([]);
        setResultUrl(null);
        setError(null);
        setTaskId(null);
        setProgressValue(0);
    };

    const getProgressLabel = (step) => {
        const map = {
            'downloading': t('audioMerger.steps.downloading'),
            'processing': t('audioMerger.steps.processing'), // "Merging..."
            'encoding': t('audioMerger.steps.encoding'),
            'uploading': t('audioMerger.steps.uploading')
        };
        return map[step] || t('audioMerger.processing');
    };

    const handlePlayAudio = (index) => {
        // Stop currently playing audio if any
        if (playingIndex !== null && audioRefs.current[playingIndex]) {
            audioRefs.current[playingIndex].pause();
            audioRefs.current[playingIndex].currentTime = 0;
        }
        
        // If clicking the same file, just stop it
        if (playingIndex === index) {
            setPlayingIndex(null);
            return;
        }
        
        // Play the selected audio
        const audioUrl = URL.createObjectURL(files[index]);
        const audio = new Audio(audioUrl);
        audioRefs.current[index] = audio;
        
        audio.play();
        setPlayingIndex(index);
        
        audio.onended = () => {
            setPlayingIndex(null);
            URL.revokeObjectURL(audioUrl);
        };
    };

    // --- RENDER ---
    return (
        <div className="p-6 md:p-8 h-full overflow-y-auto text-brand-dark bg-brand-white">
            { (user?.subscription_status !== 'active' && (!user?.is_verified || user?.credits <= 2)) ? (
                <SubscribePrompt />
            ) : user?.credits <= 0 ? (
                <PurchaseCreditsPrompt />
            ) : resultUrl ? (
                // --- RESULT VIEW ---
                <div className="max-w-2xl mx-auto text-center mt-10">
                    <div className="bg-green-50 border border-green-200 p-8 rounded-lg mb-8">
                        <CheckCircle className="text-green-500 h-16 w-16 mx-auto mb-4" />
                        <h3 className="text-2xl font-bold text-green-800 mb-2">{t('audioMerger.success')}</h3>
                        <div className="bg-white p-4 rounded-md shadow-sm mt-6">
                            <audio controls src={resultUrl} className="w-full" controlsList="nodownload" />
                        </div>
                        <a 
                            href={resultUrl} 
                            download="merged_audio.mp3"
                            className="mt-8 inline-flex items-center px-8 py-3 bg-brand-orange text-white font-bold rounded-md hover:bg-orange-600 transition shadow-lg"
                        >
                            <Download className="mr-2" size={20} /> {t('audioMerger.download')}
                        </a>
                    </div>
                    <button onClick={handleClear} className="text-slate-600 hover:text-brand-orange underline">
                        {t('audioMerger.processAnother')}
                    </button>
                </div>
            ) : (
                // --- EDITOR VIEW ---
                <div className="max-w-4xl mx-auto">
                    <h2 className="text-2xl font-semibold mb-2 text-brand-orange">{t('audioMerger.title')}</h2>
                    <p className="mt-2 text-brand-dark mb-6">{t('audioMerger.description')}</p>

                    {/* Upload Area */}
                    <div className="bg-white p-6 rounded-lg shadow-sm border border-slate-200 mb-6">
                        <div className="flex items-center p-8 border-2 border-dashed border-slate-300 rounded-md bg-slate-50 cursor-pointer hover:bg-slate-100 transition relative">
                             <label className="w-full h-full flex flex-col items-center justify-center cursor-pointer">
                                <Upload className="h-12 w-12 text-slate-400 mb-3" />
                                <span className="text-lg text-brand-orange font-semibold mb-1">
                                    {t('audioMerger.clickToUpload')}
                                </span>
                                <span className="text-sm text-slate-500">
                                    {t('audioMerger.maxSize')}
                                </span>
                                <input 
                                    type="file" 
                                    multiple 
                                    className="sr-only" 
                                    onChange={handleFileChange} 
                                    accept=".mp3,.wav,.ogg,.flac,.opus,.webm,.mp4" 
                                />
                             </label>
                        </div>
                        
                        {/* Size Indicator */}
                        <div className="mt-4 flex justify-between items-center text-sm">
                             <span className="font-medium text-slate-700">{t('audioMerger.totalSize')}:</span>
                             <span className={`${totalSizeMB > MAX_SIZE_MB ? 'text-red-600 font-bold' : 'text-slate-600'}`}>
                                {totalSizeMB.toFixed(2)} MB / {MAX_SIZE_MB} MB
                             </span>
                        </div>
                    </div>

                    {files.length > 0 && (
                        <div className="space-y-6">
                            {/* --- Files List --- */}
                            <div className="bg-white p-4 rounded-lg shadow-sm border border-slate-200">
                                <div className="flex justify-between items-center mb-4">
                                    <h3 className="font-semibold text-brand-dark">
                                        {t('audioMerger.filesList')} ({files.length})
                                    </h3>
                                    <label className="text-xs bg-slate-100 hover:bg-slate-200 text-brand-orange px-3 py-1 rounded cursor-pointer transition">
                                        <Plus size={14} className="inline mr-1" /> {t('audioMerger.addMore')}
                                        <input type="file" multiple className="hidden" onChange={handleFileChange} accept="audio/*" />
                                    </label>
                                </div>
                                
                                <div className="space-y-2">
                                    {files.map((f, idx) => (
                                        <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded border border-slate-200 hover:border-brand-orange/50 transition-colors">
                                            <div className="flex items-center overflow-hidden">
                                                <span className="bg-slate-200 text-slate-600 text-xs px-2 py-1 rounded mr-3 font-mono">#{idx + 1}</span>
                                                <FileAudio className="text-brand-orange mr-3 flex-shrink-0" size={20} />
                                                <div className="flex flex-col overflow-hidden">
                                                    <span className="font-medium text-sm truncate pr-4" title={f.name}>{f.name}</span>
                                                    <span className="text-xs text-slate-400">{(f.size / (1024*1024)).toFixed(2)} MB</span>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-2 flex-shrink-0">
                                                <button 
                                                    onClick={() => handlePlayAudio(idx)} 
                                                    className="p-1 text-brand-orange hover:bg-orange-50 rounded"
                                                    title={playingIndex === idx ? "Pause" : "Play"}
                                                >
                                                    {playingIndex === idx ? <Pause size={16} /> : <Play size={16} />}
                                                </button>
                                                <button onClick={() => moveFile(idx, -1)} disabled={idx === 0} className="p-1 text-slate-500 hover:text-brand-orange disabled:opacity-30"><ArrowUp size={16} /></button>
                                                <button onClick={() => moveFile(idx, 1)} disabled={idx === files.length - 1} className="p-1 text-slate-500 hover:text-brand-orange disabled:opacity-30"><ArrowDown size={16} /></button>
                                                <button onClick={() => removeFile(idx)} className="p-1 text-red-500 hover:bg-red-50 rounded"><Trash2 size={16} /></button>
                                            </div>
                                            </div>
                                    ))}
                                </div>
                            </div>

                            {error && (
                                <div className="bg-red-50 border border-red-200 p-4 rounded-lg">
                                    <p className="text-red-700 flex items-center"><AlertCircle size={16} className="mr-2"/>{error}</p>
                                </div>
                            )}

                            <button
                                onClick={handleProcess}
                                disabled={isProcessing || files.length < 2 || totalSizeMB > MAX_SIZE_MB}
                                className="w-full px-8 py-4 bg-brand-orange text-white font-bold text-lg rounded-md hover:bg-orange-600 transition-colors disabled:bg-slate-400 disabled:cursor-not-allowed flex items-center justify-center"
                            >
                                {isProcessing ? (
                                    <><Loader2 className="animate-spin mr-2" /> {t('audioMerger.processing')}</>
                                ) : (
                                    <><Music className="mr-2" /> {t('audioMerger.mergeButton')}</>
                                )}
                            </button>

                             {isProcessing && (
                                <div className="mt-4">
                                    <div className="flex justify-between text-xs text-slate-500 mb-1">
                                        <span>{getProgressLabel(progressStep)}</span>
                                        <span>{progressValue}%</span>
                                    </div>
                                    <div className="w-full bg-slate-200 rounded-full h-2 overflow-hidden">
                                        <div className="bg-brand-orange h-2 rounded-full transition-all duration-500" style={{ width: `${progressValue}%` }}></div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default AudioMerger;
